// Hooks
import { useFlag } from '@sketch/modules-common'
import { useHideCollectionsSetting } from 'modules/collections/hooks'
import { useSortingSettings } from 'modules/shares/hooks/useSortSettings'

// GraphQL
import { useGetProjectSharesQuery } from '@sketch/gql-types'

// Types
import { ShareSearchFilter } from 'modules/shares/hooks/useSearchFilters'

interface UseGetProjectSharesProps {
  projectIdentifier: string
  searchTerm?: string
  filters: ShareSearchFilter[]
}

export const useGetProjectShares = ({
  projectIdentifier,
  searchTerm,
  filters: baseFilters,
}: UseGetProjectSharesProps) => {
  const isSearchFilterSortOn = useFlag('search-filter-sort')
  const { hideCollections } = useHideCollectionsSetting()
  const { sort } = useSortingSettings()

  const noSearchCriteria = !searchTerm && baseFilters.length === 0

  // the `NO_COLLECTION` filter will make sure that only shares outside of
  // collections are returned. We automatically add this filter when:
  //  - `collections` is enabled AND `search-filter-sort` is enabled AND the
  //    user has not hidden collections
  //  OR
  //  - `collections` is enabled AND `search-filter-sort` is disabled AND the
  //    user is not searching the project
  const includeNoCollectionsFilter =
    (isSearchFilterSortOn && !hideCollections) ||
    (!isSearchFilterSortOn && noSearchCriteria)

  const filters = [...baseFilters]
  if (includeNoCollectionsFilter) {
    filters.push('NO_COLLECTION')
  }

  return useGetProjectSharesQuery({
    variables: {
      shortId: projectIdentifier,
      after: null,
      search: {
        name: searchTerm || null,
        isCurrentVersionDownloadable: null,
        filters,
      },
      sortOrder: isSearchFilterSortOn ? sort : undefined,
    },
    shouldInvalidatePrevious: (prev, curr) => prev?.shortId !== curr?.shortId,
  })
}
